import { inputBaseClasses, Stack, styled } from "@mui/material"
import { forwardRef, useCallback, useMemo } from "react"
import { Icon } from "../../DataDisplay/Icons"
import { ArrowDown, ArrowUp } from "./Arrows"
import { NumberField, NumberFieldProps } from "./NumberField"

export type AmountFieldProps = Omit<
    NumberFieldProps & {
        step?: number
        hideArrows?: boolean
    },
    "isAllowed" | "decimalScale" | "fixedDecimalScale"
>

const ArrowStack = styled(Stack)(() => ({
    visibility: "hidden",
    [`.${inputBaseClasses.root}:hover .${inputBaseClasses.input}~&, .${inputBaseClasses.input}:active~&, .${inputBaseClasses.input}:focus~&`]: {
        visibility: "visible",
    },
}))

export const AmountField = forwardRef<HTMLDivElement, AmountFieldProps>((props, ref) => {
    const { onBlur, min = 1, max = 9999, type, step = 1, hideArrows, InputProps, ...restProps } = props

    const decimalScale = useMemo(() => step.toString().split(".")[1]?.length ?? 0, [step])

    const handleStep = useCallback(
        (difference: number) => {
            const newVal = (props.value ?? 0) + difference
            if ((min === undefined || newVal >= min) && (max === undefined || newVal <= max)) {
                props.onChange(newVal)
            }
        },
        [props.onChange, props.value, min, max]
    )
    const handleStepDown = useCallback(() => handleStep(-step), [handleStep, step])
    const handleStepUp = useCallback(() => handleStep(step), [handleStep, step])

    const handleBlur = useCallback(
        (value: number | undefined, event: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(value ?? (props.nullable ? undefined : (min ?? 0)), event)
        },
        [min, props.nullable, onBlur]
    )

    const InputPropsWithArrows = useMemo(
        () => ({
            ...props.InputProps,
            endAdornment: (
                <>
                    {props.InputProps?.endAdornment}
                    {!hideArrows && (
                        <ArrowStack>
                            <ArrowUp className="amount_up" disabled={props.disabled} onClick={handleStepUp} disableRipple tabIndex={-1}>
                                <Icon name="up" />
                            </ArrowUp>
                            <ArrowDown className="amount_down" disabled={props.disabled} onClick={handleStepDown} disableRipple tabIndex={-1}>
                                <Icon name="down" />
                            </ArrowDown>
                        </ArrowStack>
                    )}
                </>
            ),
        }),
        [props.InputProps, handleStepDown, handleStepUp, hideArrows, props.disabled]
    )

    return <NumberField ref={ref} decimalScale={decimalScale} onBlur={handleBlur} InputProps={InputPropsWithArrows} {...restProps} />
})
